import {useCallback,useEffect,useMemo,useRef,useState} from "react";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  Stack,
  Typography
} from "@mui/material";
import {
  ArrowForwardRounded,
  ChevronLeftRounded,
  ChevronRightRounded,
  ErrorOutlineRounded,
  RefreshRounded,
  SportsSoccerRounded
} from "@mui/icons-material";
import MatchDetailsSection from "./MatchDetailsSection";

const API_BASE="/matches/public/tournaments/nexus-cup-2026";

const COLORS={
  navy:"#06132D",
  blue:"#1746A2",
  blueHover:"#2359C4",
  text:"#102244",
  muted:"#64748B",
  border:"#E3EAF3",
  soft:"#F4F7FC",
  white:"#FFFFFF"
};

const formatDate=value=>{
  if(!value) return "Fecha por definir";

  const date=new Date(`${value}T12:00:00`);

  if(Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("es-EC",{
    day:"numeric",
    month:"short",
    year:"numeric"
  }).format(date);
};

const getStatus=status=>{
  const labels={
    SCHEDULED:"Programado",
    LIVE:"En vivo",
    FINISHED:"Finalizado",
    SUSPENDED:"Suspendido",
    CANCELLED:"Cancelado"
  };

  return labels[status]||"Sin estado";
};

const hasScore=match=>(
  match?.score_local!==null&&
  match?.score_local!==undefined&&
  match?.score_visitor!==null&&
  match?.score_visitor!==undefined
);

function TeamLogo({team}){
  const [failed,setFailed]=useState(false);

  useEffect(()=>{
    setFailed(false);
  },[team?.logo]);

  return(
    <Box
      sx={{
        width:{xs:54,sm:64},
        height:{xs:54,sm:64},
        flexShrink:0,
        display:"flex",
        alignItems:"center",
        justifyContent:"center"
      }}
    >
      {team?.logo&&!failed?(
        <Box
          component="img"
          src={team.logo}
          alt={team.name||"Equipo"}
          loading="lazy"
          onError={()=>setFailed(true)}
          sx={{
            display:"block",
            width:"100%",
            height:"100%",
            objectFit:"contain"
          }}
        />
      ):(
        <SportsSoccerRounded
          sx={{
            fontSize:34,
            color:"#A8B8D2"
          }}
        />
      )}
    </Box>
  );
}

function MatchStatus({status}){
  const live=status==="LIVE";

  return(
    <Box
      sx={{
        display:"inline-flex",
        alignItems:"center",
        justifyContent:"center",
        gap:.7,
        px:1.4,
        py:.6,
        borderRadius:"999px",
        backgroundColor:live
          ?"rgba(231,65,77,.12)"
          :"#EDF2FA"
      }}
    >
      {live&&(
        <Box
          sx={{
            width:6,
            height:6,
            borderRadius:"50%",
            backgroundColor:"#EF4855"
          }}
        />
      )}

      <Typography
        sx={{
          fontSize:11,
          fontWeight:600,
          color:live?"#C62F3B":COLORS.blue
        }}
      >
        {getStatus(status)}
      </Typography>
    </Box>
  );
}

function MatchScore({match}){
  const visible=hasScore(match);

  return(
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="center"
      spacing={1}
    >
      <Typography
        sx={{
          fontSize:{xs:28,sm:34},
          fontWeight:600,
          letterSpacing:"-.055em",
          fontVariantNumeric:"tabular-nums",
          lineHeight:1,
          color:COLORS.text
        }}
      >
        {visible?match.score_local:"-"}
      </Typography>

      <Typography
        sx={{
          fontSize:19,
          fontWeight:400,
          color:"#9CAFC9"
        }}
      >
        :
      </Typography>

      <Typography
        sx={{
          fontSize:{xs:28,sm:34},
          fontWeight:600,
          letterSpacing:"-.055em",
          fontVariantNumeric:"tabular-nums",
          lineHeight:1,
          color:COLORS.text
        }}
      >
        {visible?match.score_visitor:"-"}
      </Typography>
    </Stack>
  );
}

function MatchCard({match,onSelect}){
  return(
    <Box
      component="button"
      type="button"
      onClick={()=>onSelect(match)}
      sx={{
        width:"100%",
        height:"100%",
        minWidth:0,
        display:"flex",
        flexDirection:"column",
        appearance:"none",
        fontFamily:"inherit",
        cursor:"pointer",
        textAlign:"left",
        px:{xs:2,sm:2.5},
        py:2.4,
        borderRadius:"20px",
        border:`1px solid ${COLORS.border}`,
        backgroundColor:COLORS.white,
        transition:"border-color .2s ease,background-color .2s ease",
        "&:hover":{
          borderColor:"#9DB6E4",
          backgroundColor:"#F3F7FD"
        },
        "&:focus-visible":{
          outline:`3px solid ${COLORS.blue}`,
          outlineOffset:3
        }
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="center"
        sx={{
          width:"100%",
          mb:2.5
        }}
      >
        <MatchStatus status={match.status}/>
      </Stack>

      <Box
        sx={{
          width:"100%",
          display:"grid",
          gridTemplateColumns:"minmax(0,1fr) auto minmax(0,1fr)",
          alignItems:"center",
          gap:{xs:1,sm:2}
        }}
      >
        <Stack alignItems="center" spacing={1.2} sx={{minWidth:0}}>
          <TeamLogo team={match.local}/>

          <Typography
            sx={{
              fontSize:{xs:12,sm:13},
              fontWeight:600,
              lineHeight:1.45,
              textAlign:"center",
              color:COLORS.text,
              overflowWrap:"anywhere"
            }}
          >
            {match.local?.name}
          </Typography>
        </Stack>

        <Stack alignItems="center" spacing={1.1}>
          <MatchScore match={match}/>

          <Typography
            sx={{
              fontSize:11,
              fontWeight:500,
              color:COLORS.muted
            }}
          >
            {match.start_time||"Por definir"}
          </Typography>
        </Stack>

        <Stack alignItems="center" spacing={1.2} sx={{minWidth:0}}>
          <TeamLogo team={match.visitor}/>

          <Typography
            sx={{
              fontSize:{xs:12,sm:13},
              fontWeight:600,
              lineHeight:1.45,
              textAlign:"center",
              color:COLORS.text,
              overflowWrap:"anywhere"
            }}
          >
            {match.visitor?.name}
          </Typography>
        </Stack>
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{
          width:"100%",
          mt:"auto",
          pt:2.5
        }}
      >
        <Typography
          sx={{
            fontSize:11,
            color:COLORS.muted
          }}
        >
          {formatDate(match.date)}
        </Typography>

        <Stack direction="row" alignItems="center" spacing={.4}>
          <Typography
            sx={{
              fontSize:11,
              fontWeight:600,
              color:COLORS.blue
            }}
          >
            Ver detalles
          </Typography>

          <ArrowForwardRounded
            sx={{
              fontSize:15,
              color:COLORS.blue
            }}
          />
        </Stack>
      </Stack>
    </Box>
  );
}

export default function CalendarSection(){
  const sliderRef=useRef(null);

  const [calendar,setCalendar]=useState(null);
  const [loading,setLoading]=useState(true);
  const [error,setError]=useState("");
  const [activeRound,setActiveRound]=useState("");
  const [activeGroupIndex,setActiveGroupIndex]=useState(0);
  const [selectedMatch,setSelectedMatch]=useState(null);

  const loadCalendar=useCallback(async()=>{
    setLoading(true);
    setError("");

    try{
      const response=await fetch(`${API_BASE}/calendar/`);

      if(!response.ok){
        throw new Error("No se pudo consultar el calendario.");
      }

      const result=await response.json();
      const rounds=Array.isArray(result.rounds)?result.rounds:[];

      setCalendar(result);

      setActiveRound(previous=>
        rounds.some(round=>round.key===previous)
          ?previous
          :rounds[0]?.key||""
      );
    }catch(err){
      setError(err.message||"No se pudo cargar el calendario.");
    }finally{
      setLoading(false);
    }
  },[]);

  useEffect(()=>{
    loadCalendar();
  },[loadCalendar]);

  const rounds=useMemo(()=>{
    return calendar?.rounds||[];
  },[calendar]);

  const currentRound=useMemo(()=>{
    return rounds.find(round=>round.key===activeRound)||null;
  },[rounds,activeRound]);

  const groups=useMemo(()=>{
    if(!currentRound) return [];

    const map=new Map();

    for(const match of currentRound.matches||[]){
      const key=match.group?.slug||"sin-grupo";

      if(!map.has(key)){
        map.set(key,{
          key:`${currentRound.key}-${key}`,
          name:match.group?.name||currentRound.phase.name,
          order:match.group?.order??999,
          matches:[]
        });
      }

      map.get(key).matches.push(match);
    }

    return Array.from(map.values()).sort(
      (a,b)=>a.order-b.order
    );
  },[currentRound]);

  useEffect(()=>{
    setActiveGroupIndex(0);
  },[activeRound]);

  const groupIndex=(
    activeGroupIndex>=0&&activeGroupIndex<groups.length
  )?activeGroupIndex:0;

  const activeGroup=groups[groupIndex]||null;

  const selectRound=round=>{
    setActiveRound(round.key);
    setActiveGroupIndex(0);

    const button=Array.from(
      sliderRef.current?.querySelectorAll("button")||[]
    ).find(item=>item.dataset.round===round.key);

    button?.scrollIntoView({
      behavior:"smooth",
      block:"nearest",
      inline:"center"
    });
  };

  const previousGroup=()=>{
    if(groups.length<2) return;

    setActiveGroupIndex(previous=>(
      (previous-1+groups.length)%groups.length
    ));
  };

  const nextGroup=()=>{
    if(groups.length<2) return;

    setActiveGroupIndex(previous=>(
      (previous+1)%groups.length
    ));
  };

  const openMatch=match=>{
    if(!match?.code) return;
    setSelectedMatch(match);
  };

  const closeMatch=()=>{
    setSelectedMatch(null);
  };

  return(
    <>
      <Box
        component="section"
        aria-label="Calendario de partidos"
        sx={{
          width:"100%",
          backgroundColor:COLORS.white,
          pt:{xs:13,sm:14,md:15},
          pb:{xs:7,md:9}
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            px:{xs:2.5,sm:4,md:5,lg:7}
          }}
        >
          {loading?(
            <Stack
              alignItems="center"
              justifyContent="center"
              spacing={2}
              sx={{minHeight:300}}
            >
              <CircularProgress
                size={32}
                sx={{color:COLORS.blue}}
              />

              <Typography
                sx={{
                  fontSize:13,
                  color:COLORS.muted
                }}
              >
                Cargando calendario...
              </Typography>
            </Stack>
          ):error?(
            <Stack
              alignItems="center"
              spacing={2}
              sx={{
                py:7,
                textAlign:"center"
              }}
            >
              <ErrorOutlineRounded
                sx={{
                  fontSize:40,
                  color:"#C84150"
                }}
              />

              <Typography
                sx={{
                  fontSize:16,
                  fontWeight:600,
                  color:COLORS.text
                }}
              >
                No se pudo cargar el calendario
              </Typography>

              <Typography
                sx={{
                  fontSize:13,
                  color:COLORS.muted
                }}
              >
                {error}
              </Typography>

              <Button
                onClick={loadCalendar}
                startIcon={<RefreshRounded/>}
                sx={{
                  px:3,
                  py:1.2,
                  borderRadius:"999px",
                  backgroundColor:COLORS.blue,
                  color:COLORS.white,
                  fontWeight:600,
                  textTransform:"none",
                  boxShadow:"none",
                  "&:hover":{
                    backgroundColor:COLORS.blueHover,
                    boxShadow:"none"
                  }
                }}
              >
                Reintentar
              </Button>
            </Stack>
          ):rounds.length===0?(
            <Box sx={{py:7,textAlign:"center"}}>
              <Typography
                sx={{
                  fontSize:15,
                  fontWeight:600,
                  color:COLORS.text
                }}
              >
                Todavía no hay partidos disponibles.
              </Typography>
            </Box>
          ):(
            <>
              <Typography
                component="h1"
                sx={{
                  fontSize:{xs:28,sm:35,md:40},
                  fontWeight:600,
                  letterSpacing:"-.035em",
                  lineHeight:1.2,
                  color:COLORS.text,
                  textAlign:"center",
                  mb:4
                }}
              >
                Jornadas del campeonato
              </Typography>

              <Box
                ref={sliderRef}
                sx={{
                  display:"flex",
                  alignItems:"stretch",
                  justifyContent:"safe center",
                  gap:1.5,
                  overflowX:"auto",
                  scrollBehavior:"smooth",
                  scrollbarWidth:"none",
                  pb:2,
                  "&::-webkit-scrollbar":{
                    display:"none"
                  }
                }}
              >
                {rounds.map(round=>{
                  const active=round.key===activeRound;

                  return(
                    <Box
                      key={round.key}
                      data-round={round.key}
                      component="button"
                      type="button"
                      aria-pressed={active}
                      onClick={()=>selectRound(round)}
                      sx={{
                        minWidth:{xs:155,sm:185},
                        maxWidth:210,
                        flexShrink:0,
                        px:2.5,
                        py:2.2,
                        borderRadius:"16px",
                        border:active
                          ?`1px solid ${COLORS.blue}`
                          :`1px solid ${COLORS.border}`,
                        backgroundColor:active
                          ?COLORS.navy
                          :COLORS.white,
                        cursor:"pointer",
                        textAlign:"center",
                        fontFamily:"inherit",
                        transition:"background-color .2s ease,border-color .2s ease",
                        "&:hover":{
                          backgroundColor:active
                            ?COLORS.navy
                            :COLORS.soft
                        },
                        "&:focus-visible":{
                          outline:`3px solid ${COLORS.blue}`,
                          outlineOffset:2
                        }
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize:10,
                          fontWeight:500,
                          color:active?"#AFC5EF":COLORS.muted,
                          mb:.8,
                          textTransform:"uppercase",
                          textAlign:"center"
                        }}
                      >
                        {round.phase.name}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize:14,
                          fontWeight:600,
                          color:active?COLORS.white:COLORS.text,
                          textAlign:"center"
                        }}
                      >
                        {round.name}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>

              {currentRound&&activeGroup&&(
                <Box
                  sx={{
                    mt:{xs:4,md:6},
                    maxWidth:1160,
                    mx:"auto"
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    spacing={{xs:2,sm:3}}
                    sx={{mb:3.5}}
                  >
                    {groups.length>1&&(
                      <IconButton
                        onClick={previousGroup}
                        aria-label="Grupo anterior"
                        sx={{
                          width:42,
                          height:42,
                          flexShrink:0,
                          color:COLORS.text,
                          border:`1px solid ${COLORS.border}`,
                          "&:hover":{
                            backgroundColor:COLORS.soft
                          }
                        }}
                      >
                        <ChevronLeftRounded/>
                      </IconButton>
                    )}

                    <Typography
                      sx={{
                        minWidth:{xs:130,sm:180},
                        fontSize:{xs:20,sm:24},
                        fontWeight:600,
                        textAlign:"center",
                        color:COLORS.text
                      }}
                    >
                      {activeGroup.name}
                    </Typography>

                    {groups.length>1&&(
                      <IconButton
                        onClick={nextGroup}
                        aria-label="Siguiente grupo"
                        sx={{
                          width:42,
                          height:42,
                          flexShrink:0,
                          color:COLORS.text,
                          border:`1px solid ${COLORS.border}`,
                          "&:hover":{
                            backgroundColor:COLORS.soft
                          }
                        }}
                      >
                        <ChevronRightRounded/>
                      </IconButton>
                    )}
                  </Stack>

                  <Box
                    key={activeGroup.key}
                    sx={{
                      display:"grid",
                      gridTemplateColumns:{
                        xs:"1fr",
                        sm:activeGroup.matches.length>1
                          ?"repeat(2,minmax(0,1fr))"
                          :"1fr"
                      },
                      justifyContent:"center",
                      gap:{xs:2,md:2.5},
                      maxWidth:activeGroup.matches.length===1
                        ?550
                        :1100,
                      mx:"auto"
                    }}
                  >
                    {activeGroup.matches.map(match=>(
                      <MatchCard
                        key={match.code}
                        match={match}
                        onSelect={openMatch}
                      />
                    ))}
                  </Box>

                  {groups.length>1&&(
                    <Stack
                      direction="row"
                      justifyContent="center"
                      spacing={1}
                      sx={{mt:3}}
                    >
                      {groups.map((group,index)=>(
                        <Box
                          key={group.key}
                          component="button"
                          type="button"
                          onClick={()=>setActiveGroupIndex(index)}
                          aria-label={`Mostrar ${group.name}`}
                          aria-pressed={groupIndex===index}
                          sx={{
                            width:groupIndex===index?25:8,
                            height:8,
                            p:0,
                            border:0,
                            borderRadius:"999px",
                            cursor:"pointer",
                            backgroundColor:groupIndex===index
                              ?COLORS.blue
                              :"#CAD4E4",
                            transition:"width .25s ease,background-color .25s ease"
                          }}
                        />
                      ))}
                    </Stack>
                  )}
                </Box>
              )}
            </>
          )}
        </Container>
      </Box>

      <MatchDetailsSection
        open={Boolean(selectedMatch)}
        match={selectedMatch}
        onClose={closeMatch}
      />
    </>
  );
}